import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Layout from '../components/Layout'

interface Tool {
  path: string
  name: string
  description: string
  icon: string
  color: string
  bgColor: string
}

interface Favorite {
  path: string
  name: string
  icon: string
  color: string
  borderColor: string
  hoverBorder: string
  bgColor: string
}

// Tool to Favorite conversion helper
const toolToFavorite = (tool: Tool): Favorite => {
  const colorMap: { [key: string]: { borderColor: string; hoverBorder: string } } = {
    'text-green-600 dark:text-green-400': { borderColor: 'border-green-500/20', hoverBorder: 'group-hover:border-green-500' },
    'text-purple-600 dark:text-purple-400': { borderColor: 'border-purple-500/20', hoverBorder: 'group-hover:border-purple-500' },
    'text-primary': { borderColor: 'border-primary/20', hoverBorder: 'group-hover:border-primary' },
    'text-orange-600 dark:text-orange-400': { borderColor: 'border-orange-500/20', hoverBorder: 'group-hover:border-orange-500' },
    'text-pink-600 dark:text-pink-400': { borderColor: 'border-pink-500/20', hoverBorder: 'group-hover:border-pink-500' },
    'text-cyan-600 dark:text-cyan-400': { borderColor: 'border-cyan-500/20', hoverBorder: 'group-hover:border-cyan-500' },
    'text-indigo-600 dark:text-indigo-400': { borderColor: 'border-indigo-500/20', hoverBorder: 'group-hover:border-indigo-500' },
    'text-yellow-600 dark:text-yellow-400': { borderColor: 'border-yellow-500/20', hoverBorder: 'group-hover:border-yellow-500' },
    'text-red-600 dark:text-red-400': { borderColor: 'border-red-500/20', hoverBorder: 'group-hover:border-red-500' },
    'text-teal-600 dark:text-teal-400': { borderColor: 'border-teal-500/20', hoverBorder: 'group-hover:border-teal-500' },
    'text-amber-600 dark:text-amber-400': { borderColor: 'border-amber-500/20', hoverBorder: 'group-hover:border-amber-500' },
    'text-blue-600 dark:text-blue-400': { borderColor: 'border-blue-500/20', hoverBorder: 'group-hover:border-blue-500' },
  }
  
  const colorInfo = colorMap[tool.color] || { borderColor: 'border-slate-500/20', hoverBorder: 'group-hover:border-slate-500' }
  const favColor = tool.color.replace(' dark:text-green-400', '').replace(' dark:text-purple-400', '')
    .replace(' dark:text-orange-400', '').replace(' dark:text-pink-400', '').replace(' dark:text-cyan-400', '')
    .replace(' dark:text-indigo-400', '').replace(' dark:text-yellow-400', '').replace(' dark:text-red-400', '')
    .replace(' dark:text-teal-400', '').replace(' dark:text-amber-400', '').replace(' dark:text-blue-400', '')
    .replace('text-', 'text-').replace('-600', '-500')
  
  return {
    path: tool.path,
    name: tool.name.split(' ')[0], // Use first word as short name
    icon: tool.icon,
    color: tool.color === 'text-primary' ? 'text-primary' : favColor,
    borderColor: colorInfo.borderColor,
    hoverBorder: colorInfo.hoverBorder,
    bgColor: tool.bgColor + ' dark:bg-surface-dark'
  }
}

const tools: Tool[] = [
  { 
    path: '/url-encode', 
    name: 'URL Encode', 
    description: 'Encode & Decode',
    icon: 'link', 
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-500/10'
  },
  { 
    path: '/base64', 
    name: 'Base64', 
    description: 'Text & Files',
    icon: 'code', 
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-500/10'
  },
  { 
    path: '/json-formatter', 
    name: 'JSON Format', 
    description: 'Prettify & Minify',
    icon: 'data_object', 
    color: 'text-primary',
    bgColor: 'bg-primary/10'
  },
  { 
    path: '/jwt-decoder', 
    name: 'JWT Decoder', 
    description: 'Debug Tokens',
    icon: 'token', 
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-500/10'
  },
  { 
    path: '/qr-code', 
    name: 'QR Code', 
    description: 'Generate & Scan',
    icon: 'qr_code_2', 
    color: 'text-pink-600 dark:text-pink-400',
    bgColor: 'bg-pink-500/10'
  },
  { 
    path: '/uuid-generator', 
    name: 'UUID Gen', 
    description: 'V4 Identifier',
    icon: 'fingerprint', 
    color: 'text-cyan-600 dark:text-cyan-400',
    bgColor: 'bg-cyan-500/10'
  },
  { 
    path: '/hash-generator', 
    name: 'Hash Gen', 
    description: 'MD5, SHA-256',
    icon: 'lock', 
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-500/10'
  },
  { 
    path: '/color-picker', 
    name: 'Color Picker', 
    description: 'HEX, RGB, HSL',
    icon: 'palette', 
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-500/10'
  },
  { 
    path: '/html-entity', 
    name: 'HTML Entity', 
    description: 'Encode & Decode',
    icon: 'html', 
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-500/10'
  },
  { 
    path: '/timestamp', 
    name: 'Timestamp', 
    description: 'Unix Converter',
    icon: 'schedule', 
    color: 'text-teal-600 dark:text-teal-400',
    bgColor: 'bg-teal-500/10'
  },
  { 
    path: '/text-case', 
    name: 'Text Case', 
    description: 'Case Converter',
    icon: 'text_format', 
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-500/10'
  },
  { 
    path: '/number-base', 
    name: 'Number Base', 
    description: 'Hex, Binary, Oct',
    icon: 'calculate', 
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-500/10'
  },
]

const defaultFavorites: Favorite[] = [
  { path: '/json-formatter', name: 'JSON', icon: 'data_object', color: 'text-primary', borderColor: 'border-primary/20', hoverBorder: 'group-hover:border-primary', bgColor: 'bg-primary/10 dark:bg-surface-dark' },
  { path: '/jwt-decoder', name: 'JWT', icon: 'token', color: 'text-orange-500', borderColor: 'border-orange-500/20', hoverBorder: 'group-hover:border-orange-500', bgColor: 'bg-orange-500/10 dark:bg-surface-dark' },
  { path: '/base64', name: 'Base64', icon: 'code', color: 'text-purple-500', borderColor: 'border-purple-500/20', hoverBorder: 'group-hover:border-purple-500', bgColor: 'bg-purple-500/10 dark:bg-surface-dark' },
  { path: '/url-encode', name: 'URL', icon: 'link', color: 'text-green-500', borderColor: 'border-green-500/20', hoverBorder: 'group-hover:border-green-500', bgColor: 'bg-green-500/10 dark:bg-surface-dark' },
]

const LONG_PRESS_DURATION = 500 // milliseconds

function Home() {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [favorites, setFavorites] = useState<Favorite[]>(defaultFavorites)
  const [editMode, setEditMode] = useState(false)
  const [pressedItem, setPressedItem] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const longPressTimer = useRef<NodeJS.Timeout | null>(null)
  const navigate = useNavigate()

  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('favorites')
    if (savedFavorites) {
      try {
        setFavorites(JSON.parse(savedFavorites))
      } catch {
        setFavorites(defaultFavorites)
      }
    }
  }, [])

  // Save favorites to localStorage
  const saveFavorites = useCallback((newFavorites: Favorite[]) => {
    setFavorites(newFavorites)
    localStorage.setItem('favorites', JSON.stringify(newFavorites))
  }, [])

  // Remove favorite
  const removeFavorite = useCallback((path: string) => {
    const newFavorites = favorites.filter(fav => fav.path !== path)
    saveFavorites(newFavorites)
    if (newFavorites.length === 0) {
      setEditMode(false)
    }
  }, [favorites, saveFavorites])

  // Add favorite
  const addFavorite = useCallback((tool: Tool) => {
    const newFavorite = toolToFavorite(tool)
    const newFavorites = [...favorites, newFavorite]
    saveFavorites(newFavorites)
    setShowAddModal(false)
  }, [favorites, saveFavorites])

  // Get tools not in favorites
  const availableTools = tools.filter(tool => 
    !favorites.some(fav => fav.path === tool.path)
  )

  // Long press handlers
  const handlePressStart = useCallback((path: string) => {
    setPressedItem(path)
    longPressTimer.current = setTimeout(() => {
      setEditMode(true)
      setPressedItem(null)
    }, LONG_PRESS_DURATION)
  }, [])

  const handlePressEnd = useCallback((path: string) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
    // If not in edit mode and press was short, navigate to the tool
    if (!editMode && pressedItem === path) {
      navigate(path)
    }
    setPressedItem(null)
  }, [editMode, pressedItem, navigate])

  const handlePressCancel = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
    setPressedItem(null)
  }, [])

  const filteredTools = tools.filter(tool => 
    tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tool.description.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <Layout>
      {/* Search Bar */}
      <div className="px-4 py-4 sticky top-[60px] z-40 bg-background-light dark:bg-background-dark transition-colors duration-300">
        <label className="flex flex-col h-12 w-full shadow-sm">
          <div className="flex w-full flex-1 items-stretch rounded-xl h-full bg-white dark:bg-surface-dark border border-slate-200 dark:border-slate-700 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary transition-all">
            <div className="text-slate-400 flex items-center justify-center pl-4 pr-2">
              <span className="material-symbols-outlined">search</span>
            </div>
            <input 
              className="flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-r-xl bg-transparent border-none text-slate-900 dark:text-white focus:outline-none focus:ring-0 placeholder:text-slate-400 text-base font-normal leading-normal h-full"
              placeholder={t('home.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </label>
      </div>

      {/* Favorites / Quick Access */}
      {!searchQuery && (
        <div className="flex flex-col pt-2">
          <div className="flex items-center justify-between px-4 pb-3">
            <h3 className="text-slate-900 dark:text-white tracking-tight text-lg font-bold leading-tight text-left">
              {t('home.favorites')}
            </h3>
            {favorites.length > 0 && (
              <button 
                onClick={() => setEditMode(!editMode)}
                className="text-primary text-sm font-medium"
              >
                {editMode ? t('home.done') : t('home.edit')}
              </button>
            )}
          </div>
          <div className="flex w-full overflow-x-auto no-scrollbar px-4 pb-2 pt-2">
            <div className="flex flex-row items-start justify-start gap-4 pr-2">
              {favorites.map((fav) => (
                <div
                  key={fav.path}
                  className="flex flex-col items-center gap-2 group min-w-[72px] relative pt-1"
                  onMouseDown={() => handlePressStart(fav.path)}
                  onMouseUp={() => handlePressEnd(fav.path)}
                  onMouseLeave={handlePressCancel}
                  onTouchStart={() => handlePressStart(fav.path)}
                  onTouchEnd={() => handlePressEnd(fav.path)}
                  onTouchCancel={handlePressCancel}
                >
                  {/* Delete button - shows in edit mode */}
                  {editMode && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        removeFavorite(fav.path)
                      }}
                      className="absolute top-0 right-0 z-10 w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center shadow-lg transition-transform hover:scale-110"
                    >
                      <span className="material-symbols-outlined text-white text-[14px]">close</span>
                    </button>
                  )}
                  <div 
                    className={`w-16 h-16 rounded-2xl ${fav.bgColor} flex items-center justify-center border ${fav.borderColor} ${!editMode ? fav.hoverBorder : ''} transition-colors cursor-pointer select-none ${editMode ? 'animate-wiggle' : ''}`}
                  >
                    <span className={`material-symbols-outlined ${fav.color} text-3xl`}>{fav.icon}</span>
                  </div>
                  <span className="text-slate-600 dark:text-slate-300 text-xs font-medium text-center select-none">{fav.name}</span>
                </div>
              ))}
              {!editMode && availableTools.length > 0 && (
                <button 
                  onClick={() => setShowAddModal(true)}
                  className="flex flex-col items-center gap-2 group min-w-[72px] pt-1"
                >
                  <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center border border-dashed border-slate-300 dark:border-slate-600 group-hover:border-primary transition-colors">
                    <span className="material-symbols-outlined text-slate-400 dark:text-slate-500 text-2xl">add</span>
                  </div>
                  <span className="text-slate-600 dark:text-slate-300 text-xs font-medium text-center">{t('home.add')}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* All Tools Grid */}
      <div className="flex flex-col pt-6 px-4">
        <h3 className="text-slate-900 dark:text-white tracking-tight text-lg font-bold leading-tight text-left pb-4">
          {searchQuery ? `${t('home.searchResults')} (${filteredTools.length})` : t('home.allTools')}
        </h3>
        <div className="grid grid-cols-2 gap-4">
          {filteredTools.map((tool) => (
            <Link
              key={tool.path}
              to={tool.path}
              className="group relative flex flex-col items-start gap-3 rounded-xl bg-white dark:bg-surface-dark p-5 shadow-sm border border-slate-200 dark:border-slate-800 hover:border-primary/50 dark:hover:border-primary/50 transition-all cursor-pointer"
            >
              <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${tool.bgColor} ${tool.color}`}>
                <span className="material-symbols-outlined">{tool.icon}</span>
              </div>
              <div>
                <h4 className="text-slate-900 dark:text-white font-semibold text-sm">{tool.name}</h4>
                <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">{tool.description}</p>
              </div>
              <div className="absolute top-5 right-5 text-slate-300 dark:text-slate-600 group-hover:text-primary transition-colors">
                <span className="material-symbols-outlined text-sm">arrow_forward_ios</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Bottom spacing */}
      <div className="h-8"></div>

      {/* Add Tool Modal */}
      {showAddModal && (
        <div 
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setShowAddModal(false)}
        >
          <div 
            className="w-full max-w-md bg-white dark:bg-surface-dark rounded-t-3xl p-6 pb-8 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">{t('home.addToFavorites')}</h3>
              <button 
                onClick={() => setShowAddModal(false)}
                className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <span className="material-symbols-outlined text-slate-500">close</span>
              </button>
            </div>
            
            <div className="grid grid-cols-4 gap-4 max-h-[60vh] overflow-y-auto">
              {availableTools.map((tool) => (
                <button
                  key={tool.path}
                  onClick={() => addFavorite(tool)}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <div className={`w-12 h-12 rounded-xl ${tool.bgColor} flex items-center justify-center`}>
                    <span className={`material-symbols-outlined ${tool.color} text-2xl`}>{tool.icon}</span>
                  </div>
                  <span className="text-slate-700 dark:text-slate-300 text-xs font-medium text-center leading-tight">
                    {tool.name.split(' ')[0]}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}

export default Home
